import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type JourneyId = "foundations" | "critique" | "ai-human" | "systems";

export const JOURNEY_IDS: JourneyId[] = [
  "foundations",
  "critique",
  "ai-human",
  "systems",
];

export const JOURNEYS: Array<{
  id: JourneyId;
  label: string;
  intro: string;
}> = [
  {
    id: "foundations",
    label: "Temeller",
    intro:
      "Yeniyseniz buradan başlayın. Bu sitedeki düşünme biçiminin omurgası.",
  },
  {
    id: "critique",
    label: "Sürdürülebilirlik Eleştirisi",
    intro:
      "‘Sürdürülebilir’ etiketinin altında ne var? Yeşil pazarlamadan sistem eleştirisine.",
  },
  {
    id: "ai-human",
    label: "Yapay Zekâ ve İnsan",
    intro:
      "Algoritmalar ile karar verme, dikkat ve özerklik arasındaki gerilim.",
  },
  {
    id: "systems",
    label: "Sistem Düşüncesi",
    intro:
      "Tek tek sorunlar değil, geri besleme döngüleri ve yapısal kalıplar.",
  },
];

export interface StarterStep {
  journey: JourneyId;
  position: number;
  title: string;
  url: string;
}

async function assertAdmin(supabase: any, userId: string) {
  const { data: roles } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  if (!(roles ?? []).some((r: { role: string }) => r.role === "admin")) {
    throw new Error("Unauthorized");
  }
}

export const getStarterPath = createServerFn({ method: "GET" }).handler(
  async () => {
    const { data, error } = await supabaseAdmin
      .from("starter_path")
      .select("journey, position, title, url")
      .order("journey", { ascending: true })
      .order("position", { ascending: true });
    if (error) throw error;
    return (data ?? []) as StarterStep[];
  },
);

export const saveStarterPath = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        steps: z
          .array(
            z.object({
              journey: z.enum([
                "foundations",
                "critique",
                "ai-human",
                "systems",
              ]),
              position: z.number().int().min(1).max(20),
              title: z.string().max(500).default(""),
              url: z.string().max(1000).default("#"),
            }),
          )
          .max(80),
      })
      .parse(d),
  )
  .middleware([requireSupabaseAuth])
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);

    // Only replace journeys that are present in the payload
    const touchedJourneys = Array.from(
      new Set(data.steps.map((s) => s.journey)),
    );
    if (touchedJourneys.length > 0) {
      const { error: delErr } = await supabaseAdmin
        .from("starter_path")
        .delete()
        .in("journey", touchedJourneys);
      if (delErr) throw delErr;
    }

    if (data.steps.length > 0) {
      const { error } = await supabaseAdmin.from("starter_path").insert(
        data.steps.map((s) => ({
          journey: s.journey,
          position: s.position,
          title: s.title,
          url: s.url || "#",
        })),
      );
      if (error) throw error;
    }
    return { ok: true };
  });
